<template>
  <v-container
    fluid
    class="fill-height d-flex flex-column justify-center align-center player-start-game"
    style="background: linear-gradient(135deg, #1a2842, #495e79)"
  >
    <div class="d-flex flex-column align-center mb-6">
      <v-img
        src="@/assets/mobipoly-logo.png"
        alt="Mobipoly Logo"
        height="130"
        width="130"
        class=""
        contain
      />
    </div>
    <div class="text-white mb-2 text-h6 text-center" style="font-weight: 500">
      Welcome Player
    </div>
    <div class="mb-4">
      <v-avatar size="60" color="#fff" style="opacity: 0.5">
        <v-icon size="52">mdi-account</v-icon>
      </v-avatar>
    </div>
    <div class="input-field mb-4">
      <label class="text-white mb-4">Your Name :</label>
      <v-text-field
        density="compact"
        variant="solo"
        class="input-style"
        v-model="playerName"
        flat
        placeholder="Enter your name"
        hide-details
        width="250"
      />
    </div>
    <div class="input-field mb-4">
      <label class="text-white mb-4">Choose Color :</label>
      <v-text-field
        v-model="playerColor"
        density="compact"
        variant="solo"
        flat
        class="input-style"
        placeholder="Type your color"
        hide-details
        width="250"
      />
    </div>
    <div class="input-field mb-4" style="width: 250px">
      <label class="text-white mb-4">Session/Room Code :</label>
      <div class="otp-inputs d-flex justify-center">
        <input
          v-for="(digit, index) in roomCodeDigits"
          :key="index"
          type="number"
          inputmode="numeric"
          maxlength="1"
          class="otp-digit"
          v-model="roomCodeDigits[index]"
          @input="onDigitInput(index, $event)"
          @paste.prevent
        />
      </div>
      <div v-if="errorMessage" class="error-msg">{{ errorMessage }}</div>
    </div>
    <v-btn
      flat
      class="enter-btn mt-2 mb-4"
      large
      @click="enterGame"
      width="250"
    >
      Join Room
    </v-btn>
    <v-btn
      class="back-btn"
      large
      variant="outlined"
      @click="goBack"
      width="250"
      flat
    >
      Back
    </v-btn>
  </v-container>
</template>

<script setup>
import { ref } from "vue";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "@/firebase";
import { useRouter } from "vue-router";

const playerName = ref("");
const playerColor = ref("");
const roomCodeDigits = ref(["", "", "", ""]);
const errorMessage = ref("");
const router = useRouter();

function onDigitInput(index, event) {
  const value = event.target.value;
  if (!/^\d?$/.test(value)) {
    roomCodeDigits.value[index] = "";
    return;
  }
  if (value && index < 3) {
    const nextInput = event.target.nextElementSibling;
    if (nextInput) nextInput.focus();
  }
}

async function enterGame() {
  errorMessage.value = "";
  const code = roomCodeDigits.value.join("");
  if (!playerName.value.trim()) {
    errorMessage.value = "Please enter your name.";
    return;
  }
  if (code.length !== 4) {
    errorMessage.value = "Room code must be 4 digits.";
    return;
  }
  const roomRef = doc(db, "rooms", code);
  const roomSnap = await getDoc(roomRef);
  if (!roomSnap.exists()) {
    errorMessage.value = "Room doesn't exist.";
    return;
  }
  const players = roomSnap.data().players || [];
  // Check if player name already exists in the room (case-insensitive)
  const nameExists = players.some(
    (p) => p.name.toLowerCase() === playerName.value.trim().toLowerCase()
  );
  if (nameExists) {
    errorMessage.value = "Player already exists.";
    return;
  }
  const takenIds = players.map((p) => p.id);
  let nextId = 1;
  while (takenIds.includes(nextId)) {
    nextId++;
  }
  await updateDoc(roomRef, {
    players: arrayUnion({
      id: nextId,
      name: playerName.value.trim(),
      color: playerColor.value.trim() || "blue",
      amount: 0,
    }),
  });
  router.push({
    name: "GameDashboard",
    query: {
      role: "player",
      code,
      playerId: nextId,
      playerName: playerName.value.trim(),
      playerColor: playerColor.value.trim() || "blue",
    },
  });
}

function goBack() {
  router.push("/choosescreen");
}
</script>

<style scoped>
.player-start-game .input-style .v-field {
  background: rgba(255, 255, 255, 0.13) !important;
  color: #fff !important;
  font-weight: 500;
}
.player-start-game .input-style input {
  color: #fff !important;
}
.player-start-game .input-field label {
  font-size: 1.08rem;
  font-weight: 500;
}
.player-start-game .enter-btn {
  background: linear-gradient(90deg, #1f7673 0%, #156bae 100%);
  border-radius: 0px;
  color: #fff !important;
  font-size: 1.12rem;
  font-weight: bold;
  text-transform: none;
  /* box-shadow: 0 2px 10px 0 rgba(18, 231, 255, 0.17); */
}
.player-start-game .enter-btn:hover {
  background: linear-gradient(45deg, #156bae, #1f7673);
}
.player-start-game .back-btn {
  background: transparent !important;
  border: 2px solid;
  border-image-slice: 1;
  border-width: 2px;
  border-image-source: linear-gradient(45deg, #1f7673, #156bae);
  color: #fff;
  font-weight: bold;
  text-transform: none;
  box-shadow: none;
}
/* OTP style room code inputs */
.otp-inputs {
  gap: 10px;
}
.otp-digit {
  width: 40px;
  height: 40px;
  text-align: center;
  font-size: 1.5rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.13);
  color: white;
}
.otp-digit:focus {
  outline: none;
  border-color: #12e7ff;
  box-shadow: 0 0 8px #12e7ff;
}
.error-msg {
  color: #ff6666;
  font-weight: 600;
  margin-top: 4px;
  font-size: 0.9rem;
  text-align: center;
}
</style>
